import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileUrl, pdfUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { FileUploader } from "@/components/FileUploader";
import {
  URGENCY_LABELS,
  URGENCY_STYLES,
  STAGE_LABELS,
  ROLE_LABELS,
  REPAIR_TYPE_LABELS,
  fmtDate,
  fmtDateShort,
  fmtRupiah,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { ImageGallery } from "@/components/ImageGallery";
import { ReportDocument } from "@/components/ReportDocument";
import { BapsPanel } from "@/components/BapsPanel";
import { DatePicker } from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  X,
  Download,
  FileCheck,
  CheckCircle2,
  Pencil,
  MapPin,
  Phone,
  Calendar,
  Printer,
} from "lucide-react";

function ImgGrid() {
  return null;
}

export default function ReportDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [workshops, setWorkshops] = useState([]);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [workshopId, setWorkshopId] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [approveManual, setApproveManual] = useState("");
  const [rejectNote, setRejectNote] = useState("");

  // workshop progress state
  const [stage, setStage] = useState("diperiksa");
  const [percent, setPercent] = useState(0);
  const [parts, setParts] = useState([]);
  const [repairFiles, setRepairFiles] = useState([]);
  const [estimated, setEstimated] = useState("");
  const [cost, setCost] = useState(0);
  const [serviceCost, setServiceCost] = useState(0);
  const [serviceDescription, setServiceDescription] = useState("");
  const [scopeSparepart, setScopeSparepart] = useState(false);
  const [scopeService, setScopeService] = useState(false);

  const [loadError, setLoadError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      setR(data);
      setStage(data.progress_stage || "diperiksa");
      setPercent(data.progress_percent || 0);
      setParts(data.spare_parts || []);
      setEstimated(data.estimated_completion || "");
      setCost(data.repair_cost || 0);
      setServiceCost(data.service_cost || 0);
      setServiceDescription(data.service_description || "");
      setScopeSparepart(!!(data.has_sparepart_replacement || data.repair_type === "penggantian_sparepart" || data.repair_type === "keduanya"));
      setScopeService(!!(data.has_service || data.repair_type === "service" || data.repair_type === "keduanya"));
      setWorkshopId(data.preferred_workshop_id || (data.preferred_workshop_name ? "__other__" : ""));
      setApproveManual(data.preferred_workshop_name || "");
      setRepairFiles(
        (data.repair_files || []).map((rf) => ({ id: rf.file_id, is_video: false, phase: rf.phase }))
      );
    } catch (e) {
      const code = e.response?.status;
      const msg =
        code === 403
          ? "Anda tidak memiliki akses ke laporan ini."
          : code === 404
          ? "Laporan tidak ditemukan."
          : formatApiErrorDetail(e.response?.data?.detail);
      setLoadError(msg);
      toast.error(msg);
    }
  };

  useEffect(() => {
    load();
    api.get("/workshops").then(({ data }) => setWorkshops(data)).catch(() => {});
    // eslint-disable-next-line
  }, [id]);

  if (loadError)
    return (
      <div
        data-testid="report-load-error"
        className="mx-auto max-w-md rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm"
      >
        <p className="font-display text-lg font-bold text-slate-900">{loadError}</p>
        <Button variant="outline" className="mt-4 rounded-full" onClick={() => navigate("/reports")}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Kembali ke daftar laporan
        </Button>
      </div>
    );

  if (!r) return <div className="text-sm text-slate-400">Memuat…</div>;

  const isApprover = ["approver", "admin"].includes(user.role);
  const isTech = ["technician", "admin"].includes(user.role);
  const isExternal = !r.workshop_id && !!r.workshop_name;
  const canFollowUp =
    user.role === "admin" ||
    (user.role === "technician" && r.workshop_id === user.workshop_id) ||
    (user.role === "approver" && !r.workshop_id && r.division_id === user.division_id);
  const isOwner = r.reporter_id === user.id;
  const requiresBaps = r.has_sparepart_replacement || r.repair_type === "penggantian_sparepart" || r.repair_type === "keduanya";
  const bapsBlocked = requiresBaps && r.baps_status !== "ditandatangani";
  const scopeChosen = r.has_sparepart_replacement || r.has_service || !!r.repair_type;
  const bapsTotal = (r.baps_spare_parts || []).reduce((s, p) => s + (p.total || 0), 0);
  const totalCost = (requiresBaps ? bapsTotal : 0) + (r.has_service ? (Number(serviceCost) || 0) : 0);

  const doApprove = async () => {
    const isOther = workshopId === "__other__";
    if (!isOther && !workshopId) return toast.error("Pilih bengkel tujuan");
    if (isOther && !approveManual.trim()) return toast.error("Isi nama bengkel lain");
    const payload = { note: approveNote };
    if (isOther) payload.workshop_name = approveManual.trim();
    else payload.workshop_id = workshopId;
    try {
      await api.post(`/reports/${id}/approve`, payload);
      toast.success("Laporan disetujui & diteruskan ke bengkel");
      setApproveOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doReject = async () => {
    if (!rejectNote.trim()) return toast.error("Alasan penolakan wajib diisi");
    try {
      await api.post(`/reports/${id}/reject`, { note: rejectNote });
      toast.success("Laporan ditolak");
      setRejectOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveProgress = async () => {
    try {
      await api.post(`/reports/${id}/progress`, {
        progress_stage: stage,
        progress_percent: Number(percent),
        repair_files: repairFiles.map((f) => ({ file_id: f.id, phase: f.phase || "proses" })),
        estimated_completion: estimated,
        service_cost: serviceCost === "" ? 0 : Number(serviceCost),
        service_description: serviceDescription,
      });
      toast.success("Progres perbaikan diperbarui");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const complete = async () => {
    try {
      await api.post(`/reports/${id}/complete`, {});
      toast.success("Perbaikan ditandai selesai");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveScope = async () => {
    if (!scopeSparepart && !scopeService) {
      toast.error("Pilih minimal satu jenis tindak lanjut");
      return;
    }
    try {
      await api.post(`/reports/${id}/repair-type`, {
        has_sparepart: scopeSparepart,
        has_service: scopeService,
      });
      toast.success("Jenis tindak lanjut disimpan");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const publish = async () => {
    try {
      await api.post(`/reports/${id}/publish`, {});
      toast.success("Laporan final diterbitkan");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadPdf = () => {
    window.open(pdfUrl(id), "_blank");
  };

  return (
    <div className="mx-auto max-w-[900px] space-y-6">
      <div className="print-hide flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" /> Kembali
        </button>
        <div className="flex flex-wrap gap-2">
          {isOwner && ["draft", "ditolak"].includes(r.status) && (
            <Button
              data-testid="edit-report-btn"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate(`/reports/${id}/edit`)}
            >
              <Pencil className="mr-1 h-4 w-4" /> Revisi
            </Button>
          )}
          {isApprover && r.status === "menunggu_persetujuan" && (
            <>
              <Button
                data-testid="approve-btn"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setApproveOpen(true)}
              >
                <Check className="mr-1 h-4 w-4" /> Setujui
              </Button>
              <Button
                data-testid="reject-btn"
                variant="outline"
                className="rounded-full border-rose-200 text-rose-600 hover:bg-rose-50"
                onClick={() => setRejectOpen(true)}
              >
                <X className="mr-1 h-4 w-4" /> Tolak
              </Button>
            </>
          )}
          {r.status === "selesai" && (
            <Button
              data-testid="publish-btn"
              className="rounded-full bg-slate-900 hover:bg-slate-800"
              onClick={publish}
            >
              <FileCheck className="mr-1 h-4 w-4" /> Terbitkan Laporan Final
            </Button>
          )}
          {["selesai", "final"].includes(r.status) && (
            <Button
              data-testid="download-pdf-btn"
              variant="outline"
              className="rounded-full"
              onClick={downloadPdf}
            >
              <Download className="mr-1 h-4 w-4" /> Unduh PDF
            </Button>
          )}
          <Button
            data-testid="print-document-btn"
            variant="outline"
            className="rounded-full"
            onClick={() => window.print()}
          >
            <Printer className="mr-1 h-4 w-4" /> Cetak
          </Button>
        </div>
      </div>

      {/* Dokumen resmi */}
      <ReportDocument r={r} />

      <div className="print-hide space-y-6">
        <div>
          {/* Workshop panel */}
          {["dalam_perbaikan", "selesai", "final"].includes(r.status) && (
            <div className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-base font-medium text-slate-900">
                  Tindak Lanjut Bengkel
                </h3>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  {r.workshop_name || ""}
                  {isExternal && (
                    <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                      Bengkel Lain
                    </span>
                  )}
                </span>
              </div>
              {isExternal && (
                <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  Bengkel eksternal tanpa akun teknisi. Tindak lanjut & unggah dokumentasi
                  dilakukan oleh <strong>Admin</strong> atau <strong>Approver divisi</strong>.
                </p>
              )}
              {isExternal && r.followup_pic_name && (
                <div
                  className="mt-2 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                  data-testid="followup-pic"
                >
                  <span className="text-slate-500">Penanggung Jawab (PIC)</span>
                  <span className="font-medium text-slate-900">
                    {r.followup_pic_name}
                    <span className="ml-1 text-xs font-normal text-slate-400">
                      ({ROLE_LABELS[r.followup_pic_role] || r.followup_pic_role})
                    </span>
                  </span>
                </div>
              )}

              {canFollowUp && r.status === "dalam_perbaikan" ? (
                <div className="mt-4 space-y-5">
                  {/* Jenis tindak lanjut (bisa pilih keduanya) */}
                  <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                    <Label>Jenis Tindak Lanjut Bengkel</Label>
                    <p className="mt-1 text-xs text-slate-500">Centang satu atau keduanya sesuai pekerjaan.</p>
                    <div className="mt-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <Checkbox
                          data-testid="scope-sparepart"
                          checked={scopeSparepart}
                          onCheckedChange={(v) => setScopeSparepart(!!v)}
                        />
                        Penggantian Sparepart <span className="text-xs text-slate-400">(wajib BAPS)</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm text-slate-700">
                        <Checkbox
                          data-testid="scope-service"
                          checked={scopeService}
                          onCheckedChange={(v) => setScopeService(!!v)}
                        />
                        Service (Tanpa Penggantian Sparepart)
                      </label>
                    </div>
                    <Button
                      data-testid="save-scope-btn"
                      size="sm"
                      className="mt-3 bg-cyan-700 hover:bg-cyan-800"
                      onClick={saveScope}
                    >
                      Simpan Jenis Tindak Lanjut
                    </Button>
                    {r.repair_type ? (
                      <p className="mt-2 text-xs text-slate-500">
                        Tersimpan: <span className="font-medium">{REPAIR_TYPE_LABELS[r.repair_type]}</span>
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-amber-600">Belum ada jenis tindak lanjut tersimpan.</p>
                    )}
                  </div>

                  {r.has_sparepart_replacement && <BapsPanel report={r} onChange={load} />}

                  {scopeChosen && (
                    <>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label>Tahapan</Label>
                          <Select value={stage} onValueChange={setStage}>
                            <SelectTrigger data-testid="stage-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STAGE_LABELS).map(([k, v]) => (
                                <SelectItem key={k} value={k}>
                                  {v}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Progres (%)</Label>
                          <Input
                            data-testid="percent-input"
                            type="number"
                            min={0}
                            max={100}
                            value={percent}
                            onChange={(e) => setPercent(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5 sm:col-span-2">
                          <Label>Estimasi Selesai</Label>
                          <DatePicker
                            testId="estimated-input"
                            value={estimated}
                            onChange={setEstimated}
                            placeholder="Pilih tanggal estimasi"
                          />
                        </div>
                      </div>

                      {r.has_service && (
                        <div className="space-y-3 rounded-md border border-slate-200 p-4">
                          <div className="space-y-1.5">
                            <Label>Uraian Pekerjaan Service</Label>
                            <Textarea
                              data-testid="service-description"
                              rows={2}
                              placeholder="mis. tune-up, ganti oli, servis rem…"
                              value={serviceDescription}
                              onChange={(e) => setServiceDescription(e.target.value)}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label>Biaya Jasa / Service (Rp)</Label>
                            <Input
                              data-testid="service-cost-input"
                              type="number"
                              min={0}
                              placeholder="0"
                              value={serviceCost}
                              onChange={(e) => setServiceCost(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      <div className="rounded-md bg-slate-50 px-4 py-3 text-sm">
                        {requiresBaps && (
                          <div className="flex items-center justify-between text-slate-500">
                            <span>Biaya Sparepart (BAPS)</span>
                            <span className="font-num">{fmtRupiah(bapsTotal)}</span>
                          </div>
                        )}
                        {r.has_service && (
                          <div className="mt-1 flex items-center justify-between text-slate-500">
                            <span>Biaya Jasa / Service</span>
                            <span className="font-num">{fmtRupiah(Number(serviceCost) || 0)}</span>
                          </div>
                        )}
                        <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2 font-semibold text-slate-800">
                          <span>Total Biaya Perbaikan</span>
                          <span className="font-num" data-testid="total-cost">{fmtRupiah(totalCost)}</span>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label>Dokumentasi Proses (sebelum / saat / sesudah)</Label>
                        <FileUploader
                          files={repairFiles}
                          setFiles={setRepairFiles}
                          testId="repair-uploader"
                        />
                      </div>

                      <div className="flex flex-wrap gap-3">
                        <Button
                          data-testid="save-progress-btn"
                          className="bg-[var(--sipkd-primary)] hover:bg-[var(--sipkd-primary-hover)]"
                          onClick={saveProgress}
                        >
                          Simpan Progres
                        </Button>
                        <Button
                          data-testid="complete-btn"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={complete}
                          disabled={bapsBlocked}
                          title={bapsBlocked ? "Selesaikan BAPS & unggah dokumen tertanda tangan dahulu" : ""}
                        >
                          <CheckCircle2 className="mr-1 h-4 w-4" /> Tandai Selesai
                        </Button>
                      </div>
                      {bapsBlocked && (
                        <p className="text-xs text-amber-600" data-testid="baps-block-hint">
                          BAPS harus dibuat dan dokumen bertanda tangan basah diunggah sebelum menyelesaikan perbaikan.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div>
                    <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                      <span>Tahapan: {STAGE_LABELS[r.progress_stage] || "-"}</span>
                      <span className="font-num font-semibold">{r.progress_percent || 0}%</span>
                    </div>
                    <Progress value={r.progress_percent || 0} className="h-2" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Approve dialog */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Setujui Laporan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Teruskan ke Bengkel *</Label>
              <Select value={workshopId} onValueChange={setWorkshopId}>
                <SelectTrigger data-testid="approve-workshop-select">
                  <SelectValue placeholder="Pilih bengkel…" />
                </SelectTrigger>
                <SelectContent>
                  {workshops.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__other__">Bengkel Lain (ketik manual)</SelectItem>
                </SelectContent>
              </Select>
              {workshopId === "__other__" && (
                <Input
                  data-testid="approve-manual-workshop"
                  className="mt-2"
                  placeholder="Nama bengkel lain…"
                  value={approveManual}
                  onChange={(e) => setApproveManual(e.target.value)}
                />
              )}
              {r.preferred_workshop_name && (
                <p className="text-xs text-slate-500">
                  Usulan pelapor: <span className="font-medium">{r.preferred_workshop_name}</span>
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Catatan (opsional)</Label>
              <Textarea
                data-testid="approve-note"
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              Batal
            </Button>
            <Button
              data-testid="confirm-approve-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={doApprove}
              disabled={!workshopId || (workshopId === "__other__" && !approveManual.trim())}
            >
              Setujui & Teruskan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak Laporan</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Alasan Penolakan *</Label>
            <Textarea
              data-testid="reject-note"
              rows={4}
              placeholder="Jelaskan alasan penolakan…"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Batal
            </Button>
            <Button
              data-testid="confirm-reject-btn"
              className="bg-rose-600 hover:bg-rose-700"
              onClick={doReject}
              disabled={!rejectNote.trim()}
            >
              Tolak Laporan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-700">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
        {value}
      </p>
    </div>
  );
}

function SignatureCard({ title, name, date }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 text-center shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <p className="mt-6 font-display text-base font-semibold text-slate-900">{name || "-"}</p>
      <p className="text-xs text-slate-400">{date ? fmtDate(date) : ""}</p>
    </div>
  );
}
